/**
 * Centralized Logger Utility
 * 
 * Features:
 * - Environment-aware logging (all logs in dev, warn/error in production)
 * - Automatic sensitive data sanitization and masking
 * - Error logs sent to backend
 * - Performance timing utilities
 * - User and page context tracking
 * 
 * Usage:
 *   import { logger } from './shared/utils/logger';
 *   logger.debug('Debug message', { data });
 *   logger.info('Info message', { data });
 *   logger.warn('Warning message', { data });
 *   logger.error('Error occurred', error, { context });
 */

import { sanitizeData as sanitizeLogData } from './dataSanitizer.js';
import { sendErrorToBackend } from '../services/backendErrorReporter.js';

// Log levels
const LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
};

class Logger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isProduction = process.env.NODE_ENV === 'production';
    
    // Set log level based on environment
    this.level = this.getLogLevel();
    
    // User context (set via setUser method)
    this.userId = null;
    this.username = null;
    
    // Debug mode (can be enabled via localStorage)
    this.debugMode = this.getDebugMode();
  }

  /**
   * Get log level based on environment
   */
  getLogLevel() {
    // Production: Only WARN and ERROR
    if (this.isProduction) {
      return LogLevel.WARN;
    }
    
    // Development: All logs
    if (this.isDevelopment) {
      return LogLevel.DEBUG;
    }
    
    // Default: INFO
    return LogLevel.INFO;
  }

  /**
   * Check if debug mode is enabled via localStorage
   */
  getDebugMode() {
    try {
      return localStorage.getItem('debugMode') === 'true';
    } catch {
      return false;
    }
  }

  /**
   * Set user context for all logs
   */
  setUser(userId, username) {
    this.userId = userId;
    this.username = username;
  }

  /**
   * Clear user context (on logout)
   */
  clearUser() {
    this.userId = null;
    this.username = null;
  }

  /**
   * Get common context for all logs
   */
  getContext() {
    return {
      userId: this.userId,
      username: this.username,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      path: window.location.pathname,
      environment: process.env.NODE_ENV,
    };
  }

  /**
   * Sanitize and mask data before logging
   * LAYER 1: Remove sensitive fields
   * LAYER 2: Mask sensitive patterns
   */
  sanitizeData(data) {
    if (!data) return data;
    
    try {
      return sanitizeLogData(data);
    } catch (error) {
      console.error('Error sanitizing log data:', error);
      return { sanitizationError: 'Failed to sanitize data' };
    }
  }

  /**
   * Format log message with emoji and level
   */
  formatMessage(level, message) {
    const emojis = {
      DEBUG: '🐛',
      INFO: 'ℹ️',
      WARN: '⚠️',
      ERROR: '❌'
    };
    
    const emoji = emojis[level] || '';
    const timestamp = new Date().toLocaleTimeString();
    
    return `${emoji} [${level}] [${timestamp}] ${message}`;
  }

  /**
   * DEBUG: Detailed logs for development/debugging
   * Only shown in development or when debugMode is enabled
   */
  debug(message, data = {}) {
    if (this.level > LogLevel.DEBUG && !this.debugMode) {
      return;
    }

    const sanitized = this.sanitizeData(data);
    const formatted = this.formatMessage('DEBUG', message);
    
    console.log(formatted, sanitized, this.getContext());
  }

  /**
   * INFO: General information logs
   * Shown in development, hidden in production
   */
  info(message, data = {}) {
    if (this.level > LogLevel.INFO) {
      return;
    }

    const sanitized = this.sanitizeData(data);
    const formatted = this.formatMessage('INFO', message);
    
    console.info(formatted, sanitized, this.getContext());
  }

  /**
   * WARN: Warning logs
   * Shown in development and production
   */
  warn(message, data = {}) {
    if (this.level > LogLevel.WARN) {
      return;
    }

    const sanitized = this.sanitizeData(data);
    const formatted = this.formatMessage('WARN', message);
    
    console.warn(formatted, sanitized, this.getContext());

    // Note: Warnings are not sent to backend as /api/v1/logs/message endpoint doesn't exist
    // Only errors are sent to backend via /api/v1/logs/error
  }

  /**
   * ERROR: Error logs
   * Always shown and sent to backend
   */
  error(message, error = null, context = {}) {
    if (this.level > LogLevel.ERROR) {
      return;
    }

    const sanitized = this.sanitizeData(context);
    const formatted = this.formatMessage('ERROR', message);
    
    const errorDetails = {
      message: error?.message || message,
      stack: error?.stack,
      name: error?.name,
      ...sanitized
    };
    
    console.error(formatted, errorDetails, this.getContext());

    // Always send errors to backend using backendErrorReporter
    const userContext = {
      username: this.username,
      userId: this.userId,
      isAuthenticated: !!(this.userId && this.username)
    };
    
    sendErrorToBackend(error || new Error(message), userContext, {
      ...sanitized,
      ...this.getContext(),
      logMessage: message
    }).catch(err => {
      console.error('Failed to send error to backend:', err);
    });
  }

  /**
   * Group related logs together
   */
  group(label) {
    if (this.level <= LogLevel.INFO) {
      console.group(`📁 ${label}`);
    }
  }

  /**
   * End log group
   */
  groupEnd() {
    if (this.level <= LogLevel.INFO) {
      console.groupEnd();
    }
  }

  /**
   * Display data in table format
   */
  table(data) {
    if (this.level <= LogLevel.INFO) {
      const sanitized = this.sanitizeData(data);
      console.table(sanitized);
    }
  }

  /**
   * Start performance timer
   */
  startTimer(label) {
    if (this.level <= LogLevel.DEBUG) {
      performance.mark(`${label}-start`);
    }
  }

  /**
   * End performance timer and log duration
   */
  endTimer(label, warnThreshold = 1000) {
    if (this.level > LogLevel.DEBUG) {
      return;
    }

    try {
      performance.mark(`${label}-end`);
      performance.measure(label, `${label}-start`, `${label}-end`);
      
      const measure = performance.getEntriesByName(label)[0];
      const duration = measure.duration.toFixed(2);
      
      // Warn if operation is slow
      if (measure.duration > warnThreshold) {
        this.warn(`Slow operation: ${label}`, { 
          duration: `${duration}ms`,
          threshold: `${warnThreshold}ms`
        });
      } else {
        this.debug(`Performance: ${label}`, { 
          duration: `${duration}ms` 
        });
      }
      
      // Clean up
      performance.clearMarks(`${label}-start`);
      performance.clearMarks(`${label}-end`);
      performance.clearMeasures(label);
      
      return measure.duration;
    } catch (error) {
      console.error('Error measuring performance:', error);
      return null;
    }
  }

  /**
   * Log API request
   */
  apiRequest(method, url, data = {}) {
    this.debug(`API Request: ${method} ${url}`, {
      method,
      url,
      data: this.sanitizeData(data)
    });
  }

  /**
   * Log API response
   */
  apiResponse(method, url, status, duration, data = {}) {
    const level = status >= 400 ? 'error' : status >= 300 ? 'warn' : 'info';
    
    this[level](`API Response: ${method} ${url}`, {
      method,
      url,
      status,
      duration: `${duration}ms`,
      data: this.sanitizeData(data)
    });
  }

  /**
   * Log user action/event
   */
  event(eventName, eventData = {}) {
    this.info(`Event: ${eventName}`, this.sanitizeData(eventData));
  }

  /**
   * Log component lifecycle
   */
  component(componentName, action, data = {}) {
    this.debug(`Component [${componentName}]: ${action}`, this.sanitizeData(data));
  }

  /**
   * Enable debug mode (useful for production debugging)
   */
  enableDebugMode() {
    try {
      localStorage.setItem('debugMode', 'true');
      this.debugMode = true;
      this.info('Debug mode enabled');
    } catch (error) {
      console.error('Failed to enable debug mode:', error);
    }
  }

  /**
   * Disable debug mode
   */
  disableDebugMode() {
    try {
      localStorage.removeItem('debugMode');
      this.debugMode = false;
      this.info('Debug mode disabled');
    } catch (error) {
      console.error('Failed to disable debug mode:', error);
    }
  }

}

// Create singleton instance
const logger = new Logger();

// Export singleton and class
export { logger, Logger };
export default logger;

// Enable debug mode in browser console:
// window.enableDebugMode = () => logger.enableDebugMode();
// window.disableDebugMode = () => logger.disableDebugMode();
